import type { MillsheetFieldsFragment } from '@mbfab/client-api'
import type { MillsheetSortConfig } from '@mbfab/shared'
import { isSome } from '@mbfab/shared'
import { FileSpreadsheet, TriangleAlert, Upload } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { MillsheetBulkEditFields } from './bulk-edit-dialog'
import { EMPTY_MILLSHEET_FILTER, toMillsheetRows } from './filter-state'
import type { MillsheetFilterOptions, MillsheetFilterState } from './filter-state'
import { MillsheetFilterBar } from './millsheet-filter-bar'
import { MillsheetsTable } from './millsheets-table'
import type { MillsheetBulkTarget, MillsheetItemUpdateFields, MillsheetSearch } from './millsheets-table'
import type { S3ObjectForPrint } from './print/print-types'
import type { Feedback, MillsheetRow } from './types'

import { Button } from '@/components/ui/button'
import type { DataTableSorter } from '@/components/ui/data-table/data-table'
import { PageHeader } from '@/components/ui/page-header'

export type MillsheetSource = MillsheetFieldsFragment & {
  steelOrderReferenceNumber?: string | null
}

export function MillsheetWorkspace({
  millsheets,
  loading,
  orderMatchingEnabled,
  millsheetSortConfig,
  feedback,
  onDismissFeedback,
  showPdfUpload,
  showCsvImport,
  onNavigateToUpload,
  onNavigateToCsvImport,
  unconfirmedDuplicateGroupCount = 0,
  onNavigateToDuplicates,
  onUpdateMillsheet,
  onUpdateMillsheetItem,
  onBulkEdit,
  onDelete,
  onDownloadS3Object,
  onFetchPrintImages,
  filterOptions,
  pagination,
  onChangePage,
  search,
  onChangeSearch,
  onCountAllResults,
  onFetchResultsForPrint,
}: {
  millsheets: MillsheetSource[]
  loading: boolean
  orderMatchingEnabled: boolean
  millsheetSortConfig: MillsheetSortConfig
  feedback: Feedback | null
  onDismissFeedback: () => void
  // PDFアップロード導線は admin ロールのみ（v1 と同じ制約。理由は routes/t/$tenant_id/index.tsx を参照）
  showPdfUpload: boolean
  // CSV取込画面への導線は旧 client と同じく admin ロールだけに出す
  showCsvImport: boolean
  onNavigateToUpload: () => void
  onNavigateToCsvImport: () => void
  // 重複明細（全項目が同じ明細が同一ミルシートに2件以上）の未確認グループ数。0 件のときはバッジを出さない。
  // 省略可にしているのは、重複確認に関係しない story からそのまま使えるようにするため
  unconfirmedDuplicateGroupCount?: number
  onNavigateToDuplicates?: () => void
  onUpdateMillsheet: (id: string, fields: { innerNo?: string; submissionDate?: string | null }) => Promise<boolean>
  onUpdateMillsheetItem: (id: string, fields: MillsheetItemUpdateFields) => Promise<boolean>
  onBulkEdit: (target: MillsheetBulkTarget, fields: MillsheetBulkEditFields, count: number) => Promise<boolean>
  onDelete: (id: string) => Promise<boolean>
  onDownloadS3Object: (s3ObjectId: string) => Promise<string | null>
  onFetchPrintImages: (s3ObjectIds: string[]) => Promise<S3ObjectForPrint[] | null>
  filterOptions: MillsheetFilterOptions
  pagination: { page: number; pageSize: number; totalCount: number }
  onChangePage: (page: number) => void
  // 絞り込み条件はコンテナが持つ（URL と同期し、一覧の取得条件と同じ値を初回描画から使うため）
  search: MillsheetSearch
  onChangeSearch: (search: MillsheetSearch, sorters: DataTableSorter[]) => void
  onCountAllResults: (filter: MillsheetSearch) => Promise<number | null>
  onFetchResultsForPrint: (
    target: MillsheetBulkTarget,
    sorters: DataTableSorter[],
    count: number,
  ) => Promise<MillsheetRow[] | null>
}) {
  // 入力欄に出ている条件がまだ一覧に反映されていない（入力待ち・変換中）間は、古い一覧のまま一括操作させない
  const [filterPending, setFilterPending] = useState(false)
  // ソートUXは DataTable 共通の複数列ソート（sorters は列 key を string として保持）。
  // 並び替えの実行は filter-state.ts の sortMillsheetRows（MillsheetSortConfig 依存）を維持し、
  // 呼び出し直前に toMillsheetSorters で MillsheetColumnKey へ絞り込む
  const [sorters, setSorters] = useState<DataTableSorter[]>([])

  const rows = useMemo(() => toMillsheetRows(millsheets), [millsheets])
  const { filter, freeWord } = search
  const conditionsKey = JSON.stringify(search)

  const changeFilter = (next: MillsheetFilterState) => {
    onChangeSearch({ filter: next, freeWord }, sorters)
  }
  const changeFreeWord = (next: string) => {
    onChangeSearch({ filter, freeWord: next }, sorters)
  }
  // 「すべてクリア」は詳細条件とフリーワードを同時に空へ戻す。
  // changeFilter → changeFreeWord と続けて呼ぶと、2回目がレンダー時の古い filter を
  // onChangeSearch へ送ってしまい、クリアしたはずの条件が復活する（millsheet-filter-bar.tsx のコメント参照）
  const clearAll = () => {
    onChangeSearch({ filter: EMPTY_MILLSHEET_FILTER, freeWord: '' }, sorters)
  }

  const changeSorters = (next: DataTableSorter[]) => {
    setSorters(next)
    onChangeSearch(search, next)
  }

  return (
    <div className="flex flex-col gap-5 p-6" data-testid="millsheet-workspace">
      <PageHeader
        title="ミルシート"
        description="ミルシートの検索・編集・印刷を行います。"
        actions={
          <>
            {unconfirmedDuplicateGroupCount > 0 && isSome(onNavigateToDuplicates) && (
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="border-destructive/40 text-destructive"
                data-testid="millsheet-duplicates-badge"
                onClick={onNavigateToDuplicates}
              >
                <TriangleAlert />
                未確認の重複明細 {unconfirmedDuplicateGroupCount} グループ
              </Button>
            )}
            {showCsvImport && (
              <Button
                type="button"
                variant="outline"
                size="lg"
                data-testid="millsheet-navigate-csv-import"
                onClick={onNavigateToCsvImport}
              >
                <FileSpreadsheet />
                CSV取込
              </Button>
            )}
            {showPdfUpload && (
              <Button
                type="button"
                variant="outline"
                size="lg"
                data-testid="millsheet-navigate-upload"
                onClick={onNavigateToUpload}
              >
                <Upload />
                PDFアップロード
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-col gap-4">
        <MillsheetFilterBar
          value={filter}
          onChange={changeFilter}
          onClearAll={clearAll}
          freeWord={freeWord}
          onFreeWordChange={changeFreeWord}
          onPendingChange={setFilterPending}
          options={filterOptions}
          sortConfig={millsheetSortConfig}
        />
        <MillsheetsTable
          millsheets={rows}
          loading={loading}
          sorters={sorters}
          onChangeSorters={changeSorters}
          orderMatchingEnabled={orderMatchingEnabled}
          millsheetSortConfig={millsheetSortConfig}
          feedback={feedback}
          onDismissFeedback={onDismissFeedback}
          onUpdateMillsheet={onUpdateMillsheet}
          onUpdateMillsheetItem={onUpdateMillsheetItem}
          onBulkEdit={onBulkEdit}
          onDelete={onDelete}
          onDownloadS3Object={onDownloadS3Object}
          onFetchPrintImages={onFetchPrintImages}
          pagination={pagination}
          onChangePage={onChangePage}
          filter={search}
          conditionsKey={conditionsKey}
          actionsDisabled={filterPending}
          onCountAllResults={onCountAllResults}
          onFetchResultsForPrint={onFetchResultsForPrint}
        />
      </div>
    </div>
  )
}
